/**
 * Word lists for the SQL formatter.
 *
 * Only words that are reserved in every mainstream dialect are written in capitals by
 * default: `name`, `year`, `type`, `key`, `first` and `value` are legal column names in
 * most databases, and a formatter that capitalises them changes what a reader thinks the
 * query says. Soft keywords are capitalised only where the grammar proves what they are
 * (types after `::`, inside CAST or in a CREATE TABLE definition).
 */

import type { SqlDialect } from "@/lib/tool-kit/latex-dev/sql-tokenizer";

/** Words that begin a clause: the formatter starts a new line for each. */
export const CLAUSE_WORDS: ReadonlySet<string> = new Set([
  "SELECT",
  "FROM",
  "WHERE",
  "GROUP BY",
  "HAVING",
  "WINDOW",
  "QUALIFY",
  "ORDER BY",
  "LIMIT",
  "OFFSET",
  "FETCH",
  "UNION",
  "UNION ALL",
  "UNION DISTINCT",
  "INTERSECT",
  "INTERSECT ALL",
  "EXCEPT",
  "EXCEPT ALL",
  "MINUS",
  "INSERT INTO",
  "REPLACE INTO",
  "VALUES",
  "UPDATE",
  "SET",
  "DELETE FROM",
  "WITH",
  "WITH RECURSIVE",
  "RETURNING",
  "ON CONFLICT",
  "ON DUPLICATE KEY UPDATE",
  "FOR UPDATE",
  "FOR SHARE",
]);

/** Words that begin a statement and run the rest of it onto one logical clause. */
export const STATEMENT_WORDS: ReadonlySet<string> = new Set([
  "CREATE",
  "ALTER",
  "DROP",
  "TRUNCATE",
  "GRANT",
  "REVOKE",
  "EXPLAIN",
  "ANALYZE",
  "VACUUM",
  "BEGIN",
  "COMMIT",
  "ROLLBACK",
  "USE",
  "SHOW",
  "DESCRIBE",
  "CALL",
  "EXEC",
  "EXECUTE",
  "MERGE",
  "COPY",
  "COMMENT",
  "PRAGMA",
  "DECLARE",
]);

/** Clauses whose body is a condition: AND and OR start new lines. */
export const CONDITION_CLAUSES: ReadonlySet<string> = new Set([
  "WHERE",
  "HAVING",
  "QUALIFY",
]);

/** Clauses whose body is a comma-separated list. */
export const LIST_CLAUSES: ReadonlySet<string> = new Set([
  "SELECT",
  "FROM",
  "GROUP BY",
  "ORDER BY",
  "SET",
  "VALUES",
  "RETURNING",
  "WINDOW",
  "ON DUPLICATE KEY UPDATE",
]);

/** Words that stay on the SELECT line, ahead of the column list. */
export const SELECT_MODIFIERS: ReadonlySet<string> = new Set([
  "DISTINCT",
  "ALL",
  "DISTINCTROW",
  "SQL_CALC_FOUND_ROWS",
  "STRAIGHT_JOIN",
]);

/** Words reserved in every mainstream dialect. */
export const KEYWORDS: ReadonlySet<string> = new Set([
  "ALL",
  "ALTER",
  "AND",
  "ANY",
  "AS",
  "ASC",
  "BETWEEN",
  "BY",
  "CASE",
  "CAST",
  "CHECK",
  "COLLATE",
  "CONSTRAINT",
  "CREATE",
  "CROSS",
  "DEFAULT",
  "DELETE",
  "DESC",
  "DISTINCT",
  "DROP",
  "ELSE",
  "END",
  "ESCAPE",
  "EXCEPT",
  "EXISTS",
  "FALSE",
  "FOREIGN",
  "FROM",
  "FULL",
  "GROUP",
  "HAVING",
  "ILIKE",
  "IN",
  "INNER",
  "INSERT",
  "INTERSECT",
  "INTO",
  "IS",
  "JOIN",
  "LEFT",
  "LIKE",
  "LIMIT",
  "NATURAL",
  "NOT",
  "NULL",
  "OFFSET",
  "ON",
  "OR",
  "ORDER",
  "OUTER",
  "OVER",
  "PRIMARY",
  "REFERENCES",
  "RIGHT",
  "SELECT",
  "SET",
  "TABLE",
  "THEN",
  "TO",
  "TRUE",
  "UNION",
  "UNIQUE",
  "UPDATE",
  "USING",
  "VALUES",
  "WHEN",
  "WHERE",
  "WITH",
  "RETURNING",
  "FETCH",
  "FOR",
  "IF",
  "AUTO_INCREMENT",
  "AUTOINCREMENT",
  "LATERAL",
  "PARTITION BY",
  "CURRENT_DATE",
  "CURRENT_TIME",
  "CURRENT_TIMESTAMP",
  "CURRENT_USER",
  "LOCALTIME",
  "LOCALTIMESTAMP",
]);

/** Words that are keywords only inside an OVER ( … ) window definition. */
export const WINDOW_WORDS: ReadonlySet<string> = new Set([
  "PARTITION",
  "ROWS",
  "RANGE",
  "GROUPS",
  "PRECEDING",
  "FOLLOWING",
  "CURRENT",
  "ROW",
  "UNBOUNDED",
  "EXCLUDE",
  "TIES",
  "OTHERS",
  "NULLS",
  "FIRST",
  "LAST",
]);

/** Built-in functions: capitalised when a bracket follows. */
export const FUNCTIONS: ReadonlySet<string> = new Set([
  "ABS",
  "ARRAY_AGG",
  "AVG",
  "CAST",
  "CEIL",
  "CEILING",
  "COALESCE",
  "CONCAT",
  "CONCAT_WS",
  "COUNT",
  "CURRENT_DATE",
  "CURRENT_TIMESTAMP",
  "DATE_PART",
  "DATE_TRUNC",
  "DATEADD",
  "DATEDIFF",
  "DENSE_RANK",
  "EXTRACT",
  "FIRST_VALUE",
  "FLOOR",
  "GETDATE",
  "GREATEST",
  "GROUP_CONCAT",
  "IFNULL",
  "INSTR",
  "ISNULL",
  "JSON_AGG",
  "JSON_EXTRACT",
  "LAG",
  "LAST_VALUE",
  "LEAD",
  "LEAST",
  "LEFT",
  "LEN",
  "LENGTH",
  "LOWER",
  "LTRIM",
  "MAX",
  "MIN",
  "MOD",
  "NOW",
  "NTILE",
  "NULLIF",
  "NVL",
  "POSITION",
  "POWER",
  "RANDOM",
  "RANK",
  "REPLACE",
  "RIGHT",
  "ROUND",
  "ROW_NUMBER",
  "RTRIM",
  "SQRT",
  "STRING_AGG",
  "SUBSTR",
  "SUBSTRING",
  "SUM",
  "TO_CHAR",
  "TO_DATE",
  "TO_TIMESTAMP",
  "TRIM",
  "UPPER",
  "COUNT_BIG",
  "STDDEV",
  "VARIANCE",
  "PERCENTILE_CONT",
  "PERCENTILE_DISC",
  "UNNEST",
  "ROLLUP",
  "CUBE",
  "GENERATE_SERIES",
  "JSON_BUILD_OBJECT",
  "JSONB_BUILD_OBJECT",
  "TRY_CAST",
  "CONVERT",
  "IIF",
  "CHARINDEX",
  "STRFTIME",
  "DATE",
  "IF",
]);

/** Column types: capitalised after `::`, in CAST … AS, and in a CREATE TABLE definition. */
export const TYPE_NAMES: ReadonlySet<string> = new Set([
  "BIGINT",
  "BIGSERIAL",
  "BINARY",
  "BIT",
  "BLOB",
  "BOOL",
  "BOOLEAN",
  "BYTEA",
  "CHAR",
  "CHARACTER",
  "CLOB",
  "DATE",
  "DATETIME",
  "DATETIME2",
  "DECIMAL",
  "DOUBLE",
  "FLOAT",
  "INT",
  "INT2",
  "INT4",
  "INT8",
  "INTEGER",
  "INTERVAL",
  "JSON",
  "JSONB",
  "LONGTEXT",
  "MEDIUMINT",
  "MEDIUMTEXT",
  "MONEY",
  "NCHAR",
  "NUMERIC",
  "NVARCHAR",
  "REAL",
  "SERIAL",
  "SMALLINT",
  "SMALLSERIAL",
  "TEXT",
  "TIME",
  "TIMESTAMP",
  "TIMESTAMPTZ",
  "TINYINT",
  "UUID",
  "VARBINARY",
  "VARCHAR",
  "VARCHAR2",
  "XML",
]);

/** Dialect-specific words that join the keyword list. */
export const DIALECT_KEYWORDS: Readonly<
  Record<SqlDialect, ReadonlySet<string>>
> = {
  standard: new Set(),
  postgresql: new Set(["ILIKE", "SIMILAR", "LATERAL", "RETURNING", "CONFLICT"]),
  mysql: new Set(["REGEXP", "RLIKE", "STRAIGHT_JOIN", "DUPLICATE"]),
  sqlserver: new Set(["TOP", "PERCENT", "APPLY", "GO", "NOLOCK"]),
  sqlite: new Set(["GLOB", "PRAGMA", "VACUUM", "REPLACE", "IGNORE"]),
};

/** Words capitalised in the head of a CREATE, ALTER or DROP statement. */
export const DDL_WORDS: ReadonlySet<string> = new Set([
  "TABLE",
  "VIEW",
  "INDEX",
  "SCHEMA",
  "DATABASE",
  "SEQUENCE",
  "TRIGGER",
  "FUNCTION",
  "PROCEDURE",
  "UNIQUE",
  "TEMPORARY",
  "TEMP",
  "MATERIALIZED",
  "OR",
  "REPLACE",
  "IF",
  "NOT",
  "EXISTS",
  "COLUMN",
  "ADD",
  "CONSTRAINT",
  "CASCADE",
  "RESTRICT",
  "RENAME",
  "TO",
  "MODIFY",
  "CHANGE",
  "ON",
  "USING",
  "AS",
  "ONLY",
  "CONCURRENTLY",
  "WITH",
  "TYPE",
]);

/** Words capitalised inside the column list of a CREATE TABLE. */
export const CONSTRAINT_WORDS: ReadonlySet<string> = new Set([
  "PRIMARY",
  "FOREIGN",
  "KEY",
  "REFERENCES",
  "UNIQUE",
  "CHECK",
  "CONSTRAINT",
  "DEFAULT",
  "NOT",
  "NULL",
  "AUTO_INCREMENT",
  "AUTOINCREMENT",
  "COLLATE",
  "ON",
  "DELETE",
  "UPDATE",
  "CASCADE",
  "RESTRICT",
  "SET",
  "NO",
  "ACTION",
  "GENERATED",
  "ALWAYS",
  "IDENTITY",
  "UNSIGNED",
  "ASC",
  "DESC",
  "IN",
  "AS",
]);

/** Words that make an operand expected next, so a following - or + is a sign. */
export const OPERAND_EXPECTING: ReadonlySet<string> = new Set([
  "SELECT",
  "WHERE",
  "AND",
  "OR",
  "NOT",
  "WHEN",
  "THEN",
  "ELSE",
  "IN",
  "BETWEEN",
  "ON",
  "LIMIT",
  "OFFSET",
  "VALUES",
  "SET",
  "DISTINCT",
  "ALL",
  "BY",
  "HAVING",
  "RETURN",
  "AS",
  "IS",
  "LIKE",
  "CASE",
]);

/** Join words, in the order they may appear before JOIN. */
export const JOIN_MODIFIERS: ReadonlySet<string> = new Set([
  "NATURAL",
  "INNER",
  "LEFT",
  "RIGHT",
  "FULL",
  "CROSS",
  "OUTER",
  "LATERAL",
  "SEMI",
  "ANTI",
  "ASOF",
]);
